import Cookies from "js-cookie";
import qs from "qs";
import { DAMSELFISH_TOKEN, WS_EXPIRED_TIME } from "../constants";
import { ChannelTypeEnum } from "../types";

export interface WSResponseData<T = any> {
  client_id: number;
  data?: T;
  type: number;
  ip?: string;
  error?: number;
  message: string;
  user_id: string;
  channel_type?: ChannelTypeEnum;
}

enum ConnectStatus {
  CONNECTING = "connecting",
  CONNECTED = "connected",
  // 断开连接中
  DISCONNECTING = "disconnecting",
  DISCONNECTED = "disconnected",
}

enum NetWorkStatus {
  ONLINE = "online",
  OFFLINE = "offline",
}
export class WebSocketClient {
  private wsAddress: string;
  private onConnectStatusChange?: (status: boolean) => void;
  private onMessage?: (data: WSResponseData) => void;
  private onSendMessageError?: (data: Record<string, any>) => void;
  private onError?: (data: {
    /** 连接持续时间 */
    connectionDuration: number;
    /** 连接地址 */
    path: string;
  }) => void;
  private onAutoReconnectionFailed?: (
    path: string,
    connectParams: Record<string, any>
  ) => void;
  private ws: WebSocket | null = null;
  private reconnectInterval = 1000; // 初始重连间隔为1秒
  private maxReconnectInterval = 30000; // 最大重连间隔为30秒
  private reconnectDecay = 1.5; // 退避因子
  // 心跳定时器
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private openTimes = 0;
  private autoReconnect = false;
  private connectParams?: Record<string, any>;
  // 暂存的ws，在上一个还没真正关闭的时候，下一个ws的参数就先放在这里
  private nextConnectParams: Record<string, any> | null = null;
  // 重试次数
  private retryTimes = 0;
  private connectStatus = ConnectStatus.DISCONNECTED;
  private networkStatus = NetWorkStatus.ONLINE;
  // ws未连接前如果发送了指令，那么需要先存起来
  private pendingMessages: Record<string, any>[] = [];
  constructor({
    wsAddress,
    onConnectStatusChange,
    onMessage,
    onSendMessageError,
    onError,
    onAutoReconnectionFailed,
  }: {
    wsAddress: string;
    onConnectStatusChange?: (status: boolean) => void;
    onMessage?: (data: WSResponseData) => void;
    onSendMessageError?: (data: Record<string, any>) => void;
    onError?: (data: {
      /** 连接持续时间 */
      connectionDuration: number;
      /** 连接地址 */
      path: string;
    }) => void;
    /** 自动重连失败触发 */
    onAutoReconnectionFailed?: (
      path: string,
      connectParams: Record<string, any>
    ) => void;
  }) {
    this.wsAddress = wsAddress;
    this.onConnectStatusChange = onConnectStatusChange;
    this.onMessage = onMessage;
    this.onSendMessageError = onSendMessageError;
    this.onError = onError;
    this.onAutoReconnectionFailed = onAutoReconnectionFailed;
    window.addEventListener("online", () => {
      this.networkStatus = NetWorkStatus.ONLINE;
      if (
        this.connectStatus === ConnectStatus.DISCONNECTED &&
        this.openTimes > 0
      ) {
        this.retryTimes = 0;
        this.scheduleReconnect();
      }
    });
    window.addEventListener("offline", () => {
      this.networkStatus = NetWorkStatus.OFFLINE;
    });
  }

  public isReady(): Promise<void> {
    const startTime = Date.now();
    return new Promise<void>((resolve, reject) => {
      const loop = () => {
        if (!this.ws) {
          reject(new Error("ws未链接"));
          return;
        }
        if (this.ws.readyState === this.ws.OPEN) {
          resolve();
          return;
        }
        if (Date.now() - startTime >= WS_EXPIRED_TIME) {
          reject(new Error("ws连接超时"));
        }
        setTimeout(loop, 100); // 添加延迟以避免过于频繁的检查
      };
      loop();
    });
  }

  public connect(params?: Record<string, any>) {
    if (this.ws && this.ws.readyState === this.ws.OPEN) return;
    const token = Cookies.get(DAMSELFISH_TOKEN);
    const wsParams = {
      ...(params || {}),
      token,
    };
    /** 开始连接时间 */
    let connectTime = Date.now();
    const logList = [];
    // console.log(
    //   this.wsAddress,
    //   this.connectStatus,
    //   new Date().toLocaleString()
    // );
    if (this.connectStatus === ConnectStatus.DISCONNECTING) {
      // 如果上一个ws正在关闭中，那么得先将下一个ws存起来
      this.nextConnectParams = wsParams;
      return;
    }
    this.connectParams = params;
    this.connectStatus = ConnectStatus.CONNECTING;
    this.autoReconnect = true;
    this.ws = new WebSocket(`${this.wsAddress}?${qs.stringify(wsParams)}`);
    logList.push({
      事件: "创建ws链接",
      地址: this.wsAddress,
      时间: new Date().toLocaleString(),
    });
    this.sendHeartBeat();

    this.ws.onmessage = (evt) => {
      if (evt.data !== "pong" && evt.data !== "ping") {
        const data = JSON.parse(evt.data) as WSResponseData;
        this.onMessage?.(data);
      }
    };

    this.ws.onopen = () => {
      connectTime = Date.now();
      logList.push({
        事件: "ws已连接",
        地址: this.wsAddress,
        时间: new Date().toLocaleString(),
      });
      if (this.openTimes > 0) {
        this.onConnectStatusChange?.(true);
      }
      this.openTimes++;
      this.reconnectInterval = 1000; // 重置重连间隔
      this.connectStatus = ConnectStatus.CONNECTED;
      // 如果之前有暂存的消息，那么需要发送
      if (this.pendingMessages.length > 0) {
        this.pendingMessages.forEach((message) => {
          this.sendMessage(message);
        });
        this.pendingMessages = [];
      }
    };

    this.ws.onclose = () => {
      const connectionDuration = Date.now() - connectTime;
      /** 是否手动关闭 */
      const isManualClose = this.connectStatus === ConnectStatus.DISCONNECTING;
      logList.push({
        事件: isManualClose ? "ws正常关闭" : "ws被动关闭",
        地址: this.wsAddress,
        时间: new Date().toLocaleString(),
        连接持续时间: Math.round(connectionDuration / 1000) + "s",
      });
      console.table(logList);
      this.connectStatus = ConnectStatus.DISCONNECTED;
      this.onConnectStatusChange?.(false);
      if (this.autoReconnect) this.scheduleReconnect();
      // 上一个ws关闭后，如果下一个ws有参数，那么需要连接
      if (this.nextConnectParams) {
        this.connect(this.nextConnectParams);
        this.nextConnectParams = null;
      }
    };

    this.ws.onerror = () => {
      const connectionDuration = Date.now() - connectTime;
      this.onError?.({ connectionDuration, path: this.wsAddress });
    };
  }

  public disConnect() {
    this.pendingMessages = [];
    if (this.ws) {
      this.connectStatus = ConnectStatus.DISCONNECTING;
      this.ws?.close();
      this.ws = null;
      this.autoReconnect = false;
      this.openTimes = 0;
      this.retryTimes = 0;
      this.reconnectInterval = 1000;
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
    }
  }

  public sendMessage(message: Record<string, any>) {
    if (this.networkStatus === NetWorkStatus.OFFLINE) {
      this.onSendMessageError?.(message);
      return;
    }
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error("WebSocket未连接，无法发送消息");
      this.onSendMessageError?.(message);
      this.pendingMessages.push(message);
    }
  }
  /**
   * 心跳
   */
  private sendHeartBeat() {
    if (this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === 1) {
        this.ping();
      } else {
        clearInterval(this.heartbeatTimer!);
        this.heartbeatTimer = null;
      }
    }, 8000);
  }

  private async ping() {
    await this.isReady();
    this.ws!.send("ping");
  }

  private scheduleReconnect() {
    setTimeout(() => {
      this.retryTimes++;
      if (this.retryTimes >= 5) {
        // this.disConnect();
        this.onAutoReconnectionFailed?.(this.wsAddress, this.connectParams);
        return;
      }
      this.connect(this.connectParams); // 尝试重新连接
      this.reconnectInterval = Math.min(
        this.maxReconnectInterval,
        this.reconnectInterval * this.reconnectDecay
      );
    }, this.reconnectInterval);
  }
}
